(function () {
    'use strict';

    /*
     * BestyPop Partner Product Titles
     *
     * This file changes only the visible storefront title.
     * It does not modify:
     *
     * - Shopify product title
     * - Product ID
     * - Variant ID
     * - SKU
     * - Barcode
     * - Inventory
     * - Price
     * - Cart data
     * - Checkout data
     */

    const PRODUCT_PREFIX_PATTERN =
        /^bestypop(?=\s|[-–—:|])/i;

    const STORE_CODE_PATTERN =
        /^[A-Za-z0-9_-]{3,60}$/;

    const PARTNER_CODE_PATTERN =
        /^[A-Za-z0-9_-]*BP$/i;

    const CACHE_PREFIX =
        'bestypop_storefront_name_';

    const CACHE_MAX_AGE =
        60 * 60 * 24 * 90 * 1000;

    const TITLE_SELECTORS = [
        '[data-bp-product-title]',
        '[data-product-title]',
        '.productView-title',
        '.productView-title h1',
        '.card-title',
        '.card-title .text',
        '.product-title',
        '.product-title a',
        '.previewCartItem-name',
        '.previewCartItem-name a',
        '.cart-item__name',
        '.cart-item__name a',
        '.predictive-search__item-heading',
        '.quickSearchResultsBlock .card-title',
        '.wishlist-item-name',
        '.recently-viewed-title',
        '.product-item-title',
        '.productView-stickyCart-title',
        '.productView-stickyCart-title a'
    ].join(',');

    const POWERED_BY_TEXT =
        'Powered by BestyPop™';

    const VENDOR_SELECTORS = [
        '.card-vendor .vendor-text',
        '.card-vendor > a',
        '.card-vendor a.vendor-text',
        '.sticky-brand a',
        '.productView-info-value a[href*="/vendors/"]',
        '.productView-info-value a[href*="/collections/vendors"]',
        '.productView-info-item .productView-info-value a'
    ].join(',');

    const HEADER_LOGO_SELECTORS = [
        '.header__heading-link',
        '.header-logo a',
        '.header__logo a',
        '.logo a',
        'header a[class*="logo"]',
        '.header-wrapper a[class*="logo"]'
    ].join(',');

    const HEADER_POWERED_BY_TEXT =
        'Powered by BestyPop™';

    let currentStoreCode = '';
    let currentStoreName = '';
    let titleObserver = null;
    let updateScheduled = false;
    let initializationPromise = null;

    function isMainBestyPopHomepage() {
        const normalizedPath =
            window.location.pathname
                .replace(/\/+$/, '') || '/';

        return normalizedPath === '/';
    }

    function finishBrandLoading() {
        document.documentElement.classList.remove(
            'bp-brand-loading'
        );
    }

    function cleanStoreCode(value) {
        if (!value) {
            return '';
        }

        let code =
            String(value).trim();

        try {
            code =
                decodeURIComponent(code);
        } catch (error) {
            // Keep the original value.
        }

        code = code
            .replace(/^["']|["']$/g, '')
            .replace(/^\/discount\//i, '')
            .split('?')[0]
            .split('#')[0]
            .trim();

        if (
            !STORE_CODE_PATTERN.test(code)
        ) {
            return '';
        }

        /*
         * All code comparisons are case-insensitive.
         */
        return code.toLowerCase();
    }

    function beginsWithBestyPop(value) {
        return PRODUCT_PREFIX_PATTERN.test(
            String(value || '').trim()
        );
    }

    function removeBestyPopPrefix(
        originalTitle
    ) {
        const title =
            String(
                originalTitle || ''
            ).trim();

        if (
            !title ||
            !beginsWithBestyPop(title)
        ) {
            return title;
        }

        return title
            .replace(
                PRODUCT_PREFIX_PATTERN,
                ''
            )
            .replace(
                /^[-–—:|\s]+/,
                ''
            )
            .replace(
                /\s{2,}/g,
                ' '
            )
            .trim();
    }

    function getCurrentStoreCode() {
        try {
            if (
                window.BestyPopStore &&
                typeof window
                    .BestyPopStore
                    .getCurrentStoreCode ===
                    'function'
            ) {
                return cleanStoreCode(
                    window
                        .BestyPopStore
                        .getCurrentStoreCode()
                );
            }
        } catch (error) {
            console.warn(
                'BestyPop product titles: ' +
                'Unable to read the active storefront.',
                error
            );
        }

        return '';
    }

    function getCacheKey(code) {
        return (
            CACHE_PREFIX +
            cleanStoreCode(code)
        );
    }

    function readCachedStoreName(code) {
        const cleanCode =
            cleanStoreCode(code);

        if (!cleanCode) {
            return '';
        }

        try {
            const rawValue =
                window.localStorage.getItem(
                    getCacheKey(cleanCode)
                );

            if (!rawValue) {
                return '';
            }

            const cached =
                JSON.parse(rawValue);

            if (
                !cached ||
                typeof cached !== 'object' ||
                typeof cached.name !==
                    'string'
            ) {
                return '';
            }

            const updated =
                Number(
                    cached.updated || 0
                );

            if (
                !updated ||
                Date.now() - updated >
                    CACHE_MAX_AGE
            ) {
                window.localStorage.removeItem(
                    getCacheKey(cleanCode)
                );

                return '';
            }

            return cached.name.trim();
        } catch (error) {
            return '';
        }
    }

    function saveCachedStoreName(
        code,
        name
    ) {
        const cleanCode =
            cleanStoreCode(code);

        const cleanName =
            String(
                name || ''
            ).trim();

        if (
            !cleanCode ||
            !cleanName
        ) {
            return;
        }

        try {
            window.localStorage.setItem(
                getCacheKey(cleanCode),
                JSON.stringify({
                    code: cleanCode,
                    name: cleanName,
                    updated: Date.now()
                })
            );
        } catch (error) {
            /*
             * The feature can continue without
             * localStorage caching.
             */
        }
    }

    function getNameFromCurrentPartnerPage(
        code
    ) {
        const context =
            window.BestyPopPageContext ||
            {};

        const pageType =
            String(
                context.pageType || ''
            )
                .trim()
                .toLowerCase();

        const pageHandle =
            cleanStoreCode(
                context.pageHandle
            );

        const templateSuffix =
            cleanStoreCode(
                context.pageTemplateSuffix
            );

        const pageTitle =
            String(
                context.pageTitle || ''
            ).trim();

        const cleanCode =
            cleanStoreCode(code);

        if (
            pageType !== 'page' ||
            !cleanCode ||
            !PARTNER_CODE_PATTERN.test(
                cleanCode
            ) ||
            !pageHandle ||
            !templateSuffix ||
            !pageTitle
        ) {
            return '';
        }

        /*
         * The storefront is valid only when:
         *
         * active code = page handle = template suffix
         *
         * Comparison is case-insensitive.
         */
        if (
            pageHandle === cleanCode &&
            templateSuffix === cleanCode
        ) {
            return pageTitle;
        }

        return '';
    }

    function extractStoreNameFromPage(
        pageDocument
    ) {
        if (!pageDocument) {
            return '';
        }

        /*
         * Prefer the Shopify page heading.
         */
        const heading =
            pageDocument.querySelector(
                [
                    'main h1',
                    '.page-header h1',
                    '.page-title',
                    '.page-header-title',
                    '[data-page-title]'
                ].join(',')
            );

        const headingText =
            heading?.textContent?.trim();

        if (headingText) {
            return headingText;
        }

        /*
         * Second choice: Open Graph title.
         */
        const openGraphTitle =
            pageDocument
                .querySelector(
                    'meta[property="og:title"]'
                )
                ?.getAttribute(
                    'content'
                )
                ?.trim();

        if (openGraphTitle) {
            return openGraphTitle;
        }

        /*
         * Final choice: browser page title.
         */
        const browserTitle =
            pageDocument
                .querySelector('title')
                ?.textContent
                ?.trim();

        if (!browserTitle) {
            return '';
        }

        /*
         * Example:
         *
         * The Online Brand Store – BestyPop
         *
         * becomes:
         *
         * The Online Brand Store
         */
        return browserTitle
            .replace(
                /\s+[|–—-]\s+BestyPop.*$/i,
                ''
            )
            .trim();
    }

    async function fetchStoreName(code) {
        const cleanCode =
            cleanStoreCode(code);

        if (!cleanCode) {
            return '';
        }

        /*
         * When currently on the matching storefront
         * page, Shopify already gives us the title.
         */
        const currentPageName =
            getNameFromCurrentPartnerPage(
                cleanCode
            );

        if (currentPageName) {
            saveCachedStoreName(
                cleanCode,
                currentPageName
            );

            return currentPageName;
        }

        /*
         * Use the cached storefront name when available.
         */
        const cachedName =
            readCachedStoreName(
                cleanCode
            );

        if (cachedName) {
            return cachedName;
        }

        /*
         * Shared product links may be opened before the
         * user has visited the partner homepage.
         *
         * In that case, fetch:
         *
         * /pages/dh2032bp
         *
         * and read its Shopify page title.
         */
        const storefrontPath =
            `/pages/${encodeURIComponent(
                cleanCode
            )}`;

        try {
            const response =
                await fetch(
                    storefrontPath,
                    {
                        method: 'GET',
                        credentials:
                            'same-origin',
                        headers: {
                            Accept:
                                'text/html'
                        },
                        cache: 'no-store'
                    }
                );

            if (!response.ok) {
                console.warn(
                    'BestyPop product titles: ' +
                    'No matching storefront page ' +
                    'was found for:',
                    cleanCode
                );

                return '';
            }

            const html =
                await response.text();

            const pageDocument =
                new DOMParser()
                    .parseFromString(
                        html,
                        'text/html'
                    );

            const pageName =
                extractStoreNameFromPage(
                    pageDocument
                );

            if (!pageName) {
                console.warn(
                    'BestyPop product titles: ' +
                    'The matching storefront page ' +
                    'was found, but its title ' +
                    'could not be read:',
                    cleanCode
                );

                return '';
            }

            saveCachedStoreName(
                cleanCode,
                pageName
            );

            return pageName;
        } catch (error) {
            console.warn(
                'BestyPop product titles: ' +
                'Unable to load the matching ' +
                'storefront page:',
                cleanCode,
                error
            );

            return '';
        }
    }

    function getSavedOriginalTitle(
        element
    ) {
        if (!element) {
            return '';
        }

        return (
            element.getAttribute(
                'data-bp-original-product-title'
            ) || ''
        ).trim();
    }

    function saveOriginalTitle(
        element,
        originalTitle
    ) {
        if (
            !element ||
            !originalTitle
        ) {
            return;
        }

        if (
            !element.hasAttribute(
                'data-bp-original-product-title'
            )
        ) {
            element.setAttribute(
                'data-bp-original-product-title',
                originalTitle
            );
        }
    }

    function getProductTitleAttribute(
        element
    ) {
        if (!element) {
            return '';
        }

        return (
            element.getAttribute(
                'data-product-title'
            ) || ''
        ).trim();
    }

    function findTitleTextNode(
        element
    ) {
        if (!element) {
            return null;
        }

        return Array.from(
            element.childNodes
        ).find((node) => {
            return (
                node.nodeType ===
                    Node.TEXT_NODE &&
                beginsWithBestyPop(
                    node.textContent
                )
            );
        }) || null;
    }

    function getOriginalTitle(
        element
    ) {
        if (!element) {
            return '';
        }

        const savedOriginal =
            getSavedOriginalTitle(
                element
            );

        if (savedOriginal) {
            return savedOriginal;
        }

        const attributeTitle =
            getProductTitleAttribute(
                element
            );

        if (
            beginsWithBestyPop(
                attributeTitle
            )
        ) {
            saveOriginalTitle(
                element,
                attributeTitle
            );

            return attributeTitle;
        }

        const textNode =
            findTitleTextNode(
                element
            );

        const nodeText =
            textNode
                ?.textContent
                ?.trim() || '';

        if (
            beginsWithBestyPop(
                nodeText
            )
        ) {
            saveOriginalTitle(
                element,
                nodeText
            );

            return nodeText;
        }

        const elementText =
            element
                .textContent
                ?.trim() || '';

        if (
            beginsWithBestyPop(
                elementText
            )
        ) {
            saveOriginalTitle(
                element,
                elementText
            );

            return elementText;
        }

        return '';
    }

    function getPreferredTitleElement(
        element
    ) {
        if (!element) {
            return null;
        }

        /*
         * Product cards may contain:
         *
         * <a class="card-title">
         *   <span class="text">
         *     BestyPop Product
         *     <span data-change-title>Color</span>
         *   </span>
         * </a>
         *
         * Prefer the inner .text element so the variant
         * suffix remains untouched.
         */
        if (
            element.matches(
                '.card-title'
            )
        ) {
            return (
                element.querySelector(
                    ':scope > .text'
                ) ||
                element
            );
        }

        if (
            element.matches(
                '[data-product-title]'
            )
        ) {
            return (
                element.querySelector(
                    ':scope > .text'
                ) ||
                element
            );
        }

        return element;
    }

    function updateTitleAttribute(
        element,
        displayTitle
    ) {
        if (
            !element ||
            !displayTitle
        ) {
            return;
        }

        if (
            element.hasAttribute(
                'data-product-title'
            )
        ) {
            const existingValue =
                element.getAttribute(
                    'data-product-title'
                );

            if (
                existingValue !==
                displayTitle
            ) {
                element.setAttribute(
                    'data-product-title',
                    displayTitle
                );
            }
        }

        const existingTitle =
            element.getAttribute(
                'title'
            );

        if (
            beginsWithBestyPop(
                existingTitle
            )
        ) {
            element.setAttribute(
                'title',
                displayTitle
            );
        }
    }

    function updateVisibleTitleElement(
        element
    ) {
        if (
            !element ||
            !currentStoreName
        ) {
            return;
        }

        const target =
            getPreferredTitleElement(
                element
            );

        if (!target) {
            return;
        }

        /*
         * Do not process the same title repeatedly.
         */
        const previousDisplayTitle =
            target.getAttribute(
                'data-bp-display-product-title'
            );

        if (
            previousDisplayTitle &&
            target.textContent
                ?.trim()
                .startsWith(
                    previousDisplayTitle
                )
        ) {
            updateTitleAttribute(
                element,
                previousDisplayTitle
            );

            return;
        }

        const originalTitle =
            getOriginalTitle(target) ||
            getOriginalTitle(element);

        if (!originalTitle) {
            return;
        }

        const displayTitle =
            removeBestyPopPrefix(
                originalTitle
            );

        if (
            !displayTitle ||
            displayTitle ===
                originalTitle
        ) {
            return;
        }

        const directTextNode =
            findTitleTextNode(
                target
            );

        if (directTextNode) {
            /*
             * Preserve color/variant spans and icons.
             */
            const leadingWhitespace =
                directTextNode
                    .textContent
                    .match(/^\s*/)?.[0] ||
                '';

            const trailingWhitespace =
                directTextNode
                    .textContent
                    .match(/\s*$/)?.[0] ||
                '';

            directTextNode.textContent =
                leadingWhitespace +
                displayTitle +
                trailingWhitespace;
        } else if (
            target.children.length === 0
        ) {
            target.textContent =
                displayTitle;
        } else {
            /*
             * When the title contains child elements,
             * preserve known variant suffixes.
             */
            const variantElement =
                target.querySelector(
                    '[data-change-title]'
                );

            if (variantElement) {
                const existingTextNodes =
                    Array.from(
                        target.childNodes
                    ).filter(
                        (node) =>
                            node.nodeType ===
                            Node.TEXT_NODE
                    );

                const firstTextNode =
                    existingTextNodes[0];

                if (firstTextNode) {
                    firstTextNode.textContent =
                        `${displayTitle} `;
                } else {
                    target.insertBefore(
                        document.createTextNode(
                            `${displayTitle} `
                        ),
                        target.firstChild
                    );
                }
            } else {
                /*
                 * Avoid removing unknown interactive
                 * elements. Change only when the element
                 * contains plain title text.
                 */
                const textOnly =
                    target.textContent
                        ?.trim() || '';

                if (
                    beginsWithBestyPop(
                        textOnly
                    )
                ) {
                    target.textContent =
                        displayTitle;
                }
            }
        }

        target.setAttribute(
            'data-bp-display-product-title',
            displayTitle
        );

        saveOriginalTitle(
            target,
            originalTitle
        );

        updateTitleAttribute(
            target,
            displayTitle
        );

        updateTitleAttribute(
            element,
            displayTitle
        );

        const parentTitleElement =
            element.closest(
                '[data-product-title]'
            );

        if (parentTitleElement) {
            saveOriginalTitle(
                parentTitleElement,
                originalTitle
            );

            updateTitleAttribute(
                parentTitleElement,
                displayTitle
            );
        }
    }

    function collectHeaderLogoElements(root) {
        const container =
            root instanceof Document ||
            root instanceof Element
                ? root
                : document;

        const elements = [];

        if (
            container instanceof Element &&
            container.matches(
                HEADER_LOGO_SELECTORS
            )
        ) {
            elements.push(container);
        }

        container
            .querySelectorAll?.(
                HEADER_LOGO_SELECTORS
            )
            .forEach((element) => {
                elements.push(element);
            });

        return Array.from(
            new Set(elements)
        );
    }

    function parsePixelValue(value) {
        const number =
            Number.parseFloat(
                String(value || '')
            );

        return Number.isFinite(number)
            ? number
            : 0;
    }

    function getAvailableHeaderLogoWidth(element) {
        if (!element) {
            return 0;
        }

        const elementWidth =
            element.getBoundingClientRect()
                .width;

        if (elementWidth > 0) {
            return Math.max(
                elementWidth - 8,
                0
            );
        }

        const parentWidth =
            element.parentElement
                ?.getBoundingClientRect()
                .width || 0;

        return Math.max(
            parentWidth - 8,
            0
        );
    }

    function splitBrandNameIntoTwoLines(value) {
        const words =
            String(value || '')
                .trim()
                .split(/\s+/)
                .filter(Boolean);

        if (words.length < 2) {
            return [
                String(value || '').trim(),
                ''
            ];
        }

        let bestIndex = 1;
        let smallestDifference =
            Number.POSITIVE_INFINITY;

        for (
            let index = 1;
            index < words.length;
            index += 1
        ) {
            const firstLine =
                words
                    .slice(0, index)
                    .join(' ');

            const secondLine =
                words
                    .slice(index)
                    .join(' ');

            const difference =
                Math.abs(
                    firstLine.length -
                    secondLine.length
                );

            if (
                difference <
                smallestDifference
            ) {
                smallestDifference =
                    difference;

                bestIndex =
                    index;
            }
        }

        return [
            words
                .slice(0, bestIndex)
                .join(' '),
            words
                .slice(bestIndex)
                .join(' ')
        ];
    }

    function fitHeaderBrandName(
        nameElement,
        logoElement
    ) {
        if (
            !nameElement ||
            !logoElement
        ) {
            return;
        }

        const availableWidth =
            getAvailableHeaderLogoWidth(
                logoElement
            );

        if (!availableWidth) {
            return;
        }

        const savedBaseSize =
            parsePixelValue(
                logoElement.getAttribute(
                    'data-bp-logo-font-size'
                )
            );

        const computedBaseSize =
            parsePixelValue(
                window.getComputedStyle(
                    nameElement
                ).fontSize
            );

        const baseSize =
            Math.max(
                savedBaseSize ||
                computedBaseSize ||
                26,
                24
            );

        const fullName =
            currentStoreName;

        /*
         * Reset any previous fitting.
         */
        nameElement.textContent =
            fullName;

        nameElement.style.fontSize =
            `${baseSize}px`;

        nameElement.style.lineHeight =
            '1';

        nameElement.style.whiteSpace =
            'nowrap';

        nameElement.style.width =
            'max-content';

        nameElement.style.maxWidth =
            'none';

        nameElement.style.overflow =
            'visible';

        nameElement.style.textOverflow =
            'clip';

        nameElement.style.transform =
            'none';

        nameElement.style.transformOrigin =
            'center center';

        nameElement.style.textAlign =
            'center';

        const originalSpacing =
            parsePixelValue(
                window.getComputedStyle(
                    nameElement
                ).letterSpacing
            );

        let spacing =
            Number.isFinite(
                originalSpacing
            )
                ? originalSpacing
                : 0;

        while (
            nameElement.scrollWidth >
                availableWidth &&
            spacing >
                -1.25
        ) {
            spacing -= 0.1;

            nameElement.style.letterSpacing =
                `${spacing}px`;
        }

        const renderedWidth =
            nameElement.scrollWidth;

        const requiredScale =
            renderedWidth > 0
                ? availableWidth /
                    renderedWidth
                : 1;

        /*
         * Keep short and medium names on one line.
         * Only a gentle horizontal compression is used.
         */
        if (requiredScale >= 0.82) {
            const scaleX =
                Math.min(
                    1,
                    requiredScale
                );

            nameElement.style.transform =
                `scaleX(${scaleX})`;

            nameElement.setAttribute(
                'data-bp-header-layout',
                'single-line'
            );

            return;
        }

        /*
         * Long names become two balanced lines instead
         * of turning tiny or showing an early ellipsis.
         */
        const [
            firstLine,
            secondLine
        ] =
            splitBrandNameIntoTwoLines(
                fullName
            );

        nameElement.textContent = '';

        const firstLineElement =
            document.createElement(
                'span'
            );

        firstLineElement.textContent =
            firstLine;

        firstLineElement.style.display =
            'block';

        firstLineElement.style.whiteSpace =
            'nowrap';

        const secondLineElement =
            document.createElement(
                'span'
            );

        secondLineElement.textContent =
            secondLine;

        secondLineElement.style.display =
            secondLine
                ? 'block'
                : 'none';

        secondLineElement.style.whiteSpace =
            'nowrap';

        nameElement.appendChild(
            firstLineElement
        );

        nameElement.appendChild(
            secondLineElement
        );

        const twoLineSize =
            Math.max(
                Math.round(
                    baseSize * 0.72
                ),
                17
            );

        nameElement.style.fontSize =
            `${twoLineSize}px`;

        nameElement.style.lineHeight =
            '0.92';

        nameElement.style.whiteSpace =
            'normal';

        nameElement.style.width =
            `${availableWidth}px`;

        nameElement.style.maxWidth =
            `${availableWidth}px`;

        nameElement.style.overflow =
            'visible';

        nameElement.style.transform =
            'none';

        nameElement.style.letterSpacing =
            '-0.35px';

        nameElement.setAttribute(
            'data-bp-header-layout',
            'two-line'
        );
    }

    function scheduleHeaderBrandFit(
        nameElement,
        logoElement
    ) {
        window.requestAnimationFrame(
            () => {
                fitHeaderBrandName(
                    nameElement,
                    logoElement
                );
            }
        );
    }

    function updateHeaderLogoElement(element) {
        if (
            !element ||
            !currentStoreName
        ) {
            return;
        }

        /*
         * Do not overwrite an uploaded image/SVG logo.
         * This feature targets the text logo shown in
         * the current mobile header.
         */
        if (
            element.querySelector(
                'img, svg, picture'
            )
        ) {
            return;
        }

        if (
            typeof element.__bpOriginalHeaderHTML !==
                'string'
        ) {
            element.__bpOriginalHeaderHTML =
                element.innerHTML;
        }

        if (
            !element.hasAttribute(
                'data-bp-original-header-logo'
            )
        ) {
            element.setAttribute(
                'data-bp-original-header-logo',
                String(
                    element.textContent || ''
                ).trim()
            );
        }

        const existingName =
            element.querySelector(
                '.bp-partner-header-name'
            );

        const existingPoweredBy =
            element.querySelector(
                '.bp-partner-header-powered'
            );

        if (
            existingName &&
            existingPoweredBy
        ) {
            existingName.textContent =
                currentStoreName;

            existingPoweredBy.textContent =
                HEADER_POWERED_BY_TEXT;

            scheduleHeaderBrandFit(
                existingName,
                element
            );

            return;
        }

        element.textContent = '';

        const wrapper =
            document.createElement('span');

        wrapper.className =
            'bp-partner-header-brand';

        wrapper.style.display =
            'flex';

        wrapper.style.flexDirection =
            'column';

        wrapper.style.alignItems =
            'center';

        wrapper.style.justifyContent =
            'center';

        wrapper.style.lineHeight =
            '1.05';

        wrapper.style.textAlign =
            'center';

        const originalStyle =
            window.getComputedStyle(element);

        const name =
            document.createElement('span');

        name.className =
            'bp-partner-header-name';

        name.textContent =
            currentStoreName;

        name.style.display =
            'block';

        name.style.fontFamily =
            originalStyle.fontFamily;

        name.style.fontSize =
            originalStyle.fontSize;

        name.style.fontWeight =
            '700';

        name.style.color =
            '#111111';

        name.style.fontStyle =
            originalStyle.fontStyle;

        name.style.letterSpacing =
            originalStyle.letterSpacing;

        name.style.textTransform =
            originalStyle.textTransform;

        name.style.lineHeight =
            originalStyle.lineHeight;

        name.style.whiteSpace =
            'nowrap';

        name.style.maxWidth =
            '100%';

        name.style.overflow =
            'hidden';

        name.style.textOverflow =
            'ellipsis';

        const powered =
            document.createElement('span');

        powered.className =
            'bp-partner-header-powered';

        powered.textContent =
            HEADER_POWERED_BY_TEXT;

        powered.style.display =
            'block';

        powered.style.marginTop =
            '4px';

        powered.style.fontFamily =
            originalStyle.fontFamily;

        powered.style.fontSize =
            '10px';

        powered.style.fontWeight =
            '700';

        powered.style.lineHeight =
            '1';

        powered.style.letterSpacing =
            '0.03em';

        powered.style.textTransform =
            'none';

        powered.style.whiteSpace =
            'nowrap';

        powered.style.color =
            '#111111';

        powered.style.opacity =
            '0.85';

        wrapper.appendChild(name);
        wrapper.appendChild(powered);
        element.appendChild(wrapper);

        scheduleHeaderBrandFit(
            name,
            element
        );

        element.setAttribute(
            'aria-label',
            `${currentStoreName}. ${HEADER_POWERED_BY_TEXT}`
        );
    }

    function restoreHeaderLogoElement(element) {
        if (!element) {
            return;
        }

        if (
            typeof element.__bpOriginalHeaderHTML ===
                'string'
        ) {
            element.innerHTML =
                element.__bpOriginalHeaderHTML;
        } else {
            const originalText =
                element.getAttribute(
                    'data-bp-original-header-logo'
                );

            if (originalText) {
                element.textContent =
                    originalText;
            }
        }

        element.removeAttribute(
            'aria-label'
        );
    }

    function restoreMainStoreHeader(root) {
        collectHeaderLogoElements(root)
            .forEach((element) => {
                restoreHeaderLogoElement(
                    element
                );
            });
    }

    function updateHeaderBranding(root) {
        if (isMainBestyPopHomepage()) {
            restoreMainStoreHeader(
                root || document
            );

            finishBrandLoading();
            return;
        }

        if (!currentStoreName) {
            finishBrandLoading();
            return;
        }

        collectHeaderLogoElements(root)
            .forEach((element) => {
                updateHeaderLogoElement(
                    element
                );
            });

        finishBrandLoading();
    }

    function collectVendorElements(root) {
        const container =
            root instanceof Document ||
            root instanceof Element
                ? root
                : document;

        const elements = [];

        if (
            container instanceof
                Element &&
            container.matches(
                VENDOR_SELECTORS
            )
        ) {
            elements.push(
                container
            );
        }

        container
            .querySelectorAll?.(
                VENDOR_SELECTORS
            )
            .forEach((element) => {
                elements.push(
                    element
                );
            });

        return Array.from(
            new Set(elements)
        );
    }

    function updateVisibleVendorElement(
        element
    ) {
        if (
            !element ||
            !currentStoreName
        ) {
            return;
        }

        if (
            !element.hasAttribute(
                'data-bp-original-vendor'
            )
        ) {
            element.setAttribute(
                'data-bp-original-vendor',
                String(
                    element.textContent || ''
                ).trim()
            );
        }

        if (
            String(
                element.textContent || ''
            ).trim() !== POWERED_BY_TEXT
        ) {
            element.textContent =
                POWERED_BY_TEXT;
        }

        if (
            element.tagName === 'A'
        ) {
            element.removeAttribute(
                'href'
            );

            element.removeAttribute(
                'title'
            );

            element.setAttribute(
                'aria-label',
                POWERED_BY_TEXT
            );

            element.setAttribute(
                'role',
                'text'
            );

            element.style.pointerEvents =
                'none';

            element.style.cursor =
                'default';
        }
    }

    function updateVisibleVendorLabels(
        root
    ) {
        if (!currentStoreName) {
            return;
        }

        collectVendorElements(root)
            .forEach((element) => {
                updateVisibleVendorElement(
                    element
                );
            });
    }

    function collectTitleElements(root) {
        const container =
            root instanceof Document ||
            root instanceof Element
                ? root
                : document;

        const elements = [];

        if (
            container instanceof
                Element &&
            container.matches(
                TITLE_SELECTORS
            )
        ) {
            elements.push(
                container
            );
        }

        container
            .querySelectorAll?.(
                TITLE_SELECTORS
            )
            .forEach((element) => {
                elements.push(
                    element
                );
            });

        return Array.from(
            new Set(elements)
        );
    }

    function updateVisibleProductTitles(
        root
    ) {
        if (!currentStoreName) {
            return;
        }

        collectTitleElements(root)
            .forEach((element) => {
                updateVisibleTitleElement(
                    element
                );
            });
    }

    function scheduleTitleUpdate(root) {
        if (updateScheduled) {
            return;
        }

        updateScheduled = true;

        window.requestAnimationFrame(
            () => {
                updateScheduled = false;

                updateVisibleProductTitles(
                    root || document
                );

                updateVisibleVendorLabels(
                    root || document
                );

                updateHeaderBranding(
                    root || document
                );
            }
        );
    }

    function installTitleObserver() {
        if (titleObserver) {
            return;
        }

        titleObserver =
            new MutationObserver(
                (mutations) => {
                    let needsUpdate =
                        false;

                    for (
                        const mutation
                        of mutations
                    ) {
                        /*
                         * Ignore mutations generated by
                         * this script's own tracking
                         * attributes.
                         */
                        if (
                            mutation.type ===
                                'attributes' &&
                            (
                                mutation.attributeName ===
                                    'data-bp-original-product-title' ||
                                mutation.attributeName ===
                                    'data-bp-display-product-title'
                            )
                        ) {
                            continue;
                        }

                        if (
                            mutation.type ===
                                'childList' &&
                            mutation
                                .addedNodes
                                .length > 0
                        ) {
                            needsUpdate =
                                true;
                            break;
                        }

                        if (
                            mutation.type ===
                                'characterData'
                        ) {
                            needsUpdate =
                                true;
                            break;
                        }

                        if (
                            mutation.type ===
                                'attributes' &&
                            mutation.attributeName ===
                                'data-product-title'
                        ) {
                            needsUpdate =
                                true;
                            break;
                        }
                    }

                    if (needsUpdate) {
                        scheduleTitleUpdate(
                            document
                        );
                    }
                }
            );

        titleObserver.observe(
            document.documentElement,
            {
                childList: true,
                subtree: true,
                characterData: true,
                attributes: true,
                attributeFilter: [
                    'data-product-title'
                ]
            }
        );
    }

    async function initializeVirtualTitles() {
        if (initializationPromise) {
            return initializationPromise;
        }

        initializationPromise =
            (async () => {
                if (isMainBestyPopHomepage()) {
                    currentStoreCode = '';
                    currentStoreName = '';

                    restoreMainStoreHeader(
                        document
                    );

                    finishBrandLoading();
                    return '';
                }

                const storeCode =
                    getCurrentStoreCode();

                if (!storeCode) {
                    finishBrandLoading();
                    return '';
                }

                currentStoreCode =
                    storeCode;

                const storeName =
                    await fetchStoreName(
                        storeCode
                    );

                /*
                 * Safe fallback:
                 *
                 * If the page handle/template does not
                 * match the code, or the page cannot be
                 * found, keep the original BestyPop title.
                 */
                if (!storeName) {
                    finishBrandLoading();
                    return '';
                }

                currentStoreName =
                    storeName;

                updateVisibleProductTitles(
                    document
                );

                updateVisibleVendorLabels(
                    document
                );

                updateHeaderBranding(
                    document
                );

                installTitleObserver();

                window.dispatchEvent(
                    new CustomEvent(
                        'bestypop:store-name-ready',
                        {
                            detail: {
                                code:
                                    currentStoreCode,
                                name:
                                    currentStoreName
                            }
                        }
                    )
                );

                return currentStoreName;
            })();

        try {
            return await initializationPromise;
        } finally {
            initializationPromise = null;
        }
    }

    window.BestyPopProductTitles = {
        getCurrentStoreCode() {
            return currentStoreCode;
        },

        getCurrentStoreName() {
            return currentStoreName;
        },

        getDisplayProductTitle(
            originalTitle
        ) {
            if (!currentStoreName) {
                return String(
                    originalTitle || ''
                ).trim();
            }

            return removeBestyPopPrefix(
                originalTitle
            );
        },

        refresh(root) {
            scheduleTitleUpdate(
                root || document
            );
        },

        async reloadStoreName() {
            if (isMainBestyPopHomepage()) {
                currentStoreCode = '';
                currentStoreName = '';

                restoreMainStoreHeader(
                    document
                );

                finishBrandLoading();
                return '';
            }

            currentStoreCode =
                getCurrentStoreCode();

            currentStoreName = '';

            if (!currentStoreCode) {
                return '';
            }

            try {
                window.localStorage.removeItem(
                    getCacheKey(
                        currentStoreCode
                    )
                );
            } catch (error) {
                // Ignore cache access errors.
            }

            currentStoreName =
                await fetchStoreName(
                    currentStoreCode
                );

            if (currentStoreName) {
                updateVisibleProductTitles(
                    document
                );

                updateVisibleVendorLabels(
                    document
                );

                updateHeaderBranding(
                    document
                );

                installTitleObserver();
            }

            return currentStoreName;
        }
    };

    let headerResizeTimer = null;

    window.addEventListener(
        'resize',
        () => {
            if (headerResizeTimer) {
                window.clearTimeout(
                    headerResizeTimer
                );
            }

            headerResizeTimer =
                window.setTimeout(
                    () => {
                        updateHeaderBranding(
                            document
                        );
                    },
                    120
                );
        }
    );

    /*
     * partner-store.js normally dispatches this event.
     */
    window.addEventListener(
        'bestypop:store-ready',
        initializeVirtualTitles,
        {
            once: true
        }
    );

    /*
     * Also initialize after the HTML is ready in case
     * the store-ready event occurred before this file
     * finished loading.
     */
    if (
        document.readyState ===
        'loading'
    ) {
        document.addEventListener(
            'DOMContentLoaded',
            initializeVirtualTitles,
            {
                once: true
            }
        );
    } else {
        initializeVirtualTitles();
    }
})();  